import type { Arealplan, ArealplanDomains, Planstatus } from '@origo/planregister';
import { domainLabel, erIkkeGjeldende } from '@origo/planregister';
import type { PlanSummary, PlanBadge } from '../types';
import { formatDateNb } from './formatDate';

/** Fargevariant for planstatus-badge. Ren UI-logikk (ikke i tjenestens domene). */
function planstatusVariant(code: Planstatus): PlanBadge['variant'] {
    // Opphevet, Utgått/erstattet og Overstyrt — samme regel som «Ikke gjeldende».
    if (erIkkeGjeldende(code)) return 'danger';
    switch (code) {
        case '3': // Endelig vedtatt
        case '6': // Vedtatt med utsatt rettsvirkning
        case '7': // Endelig vedtatt med tidsbegrensning
            return 'success';
        case '1': // Planlegging igangsatt
        case '2': // Planforslag
            return 'info';
        default:
            return 'default';
    }
}

/**
 * Mapper en Arealplan til kort-visning. Badge-tekster hentes fra tjenestens
 * coded value domains (faller tilbake til rå kode hvis domenet ikke er lastet).
 */
export function toPlanSummary(plan: Arealplan, domains?: ArealplanDomains): PlanSummary {
    return {
        nasjonalArealplanId: plan.nasjonalArealplanId,
        plannavn: plan.plannavn,
        sistEndret: formatDateNb(plan.vedtaksdato ?? plan.ikrafttredelsesdato),
        // sistEndretAv + omraderCount er ikke i domain — skjules i kortet (UI tåler undefined)
        sistEndretAv: undefined,
        omraderCount: undefined,
        badges: [
            { label: domainLabel(domains, 'plantype', plan.plantype), variant: 'info' },
            { label: domainLabel(domains, 'planstatus', plan.planstatus), variant: planstatusVariant(plan.planstatus) },
        ],
        ikkeGjeldende: erIkkeGjeldende(plan.planstatus),
    };
}

/**
 * Lager en `toPlanSummary` som cacher resultatet per Arealplan-objekt. React
 * Query beholder objektene for sider som allerede er lastet, så kortene får
 * samme `plan`-referanse når en ny side kommer til — og `React.memo` på
 * `PlanResultCard` slipper å rendre dem på nytt.
 */
export function createPlanSummaryMapper(domains?: ArealplanDomains): (plan: Arealplan) => PlanSummary {
    const cache = new WeakMap<Arealplan, PlanSummary>();
    return plan => {
        let summary = cache.get(plan);
        if (!summary) {
            summary = toPlanSummary(plan, domains);
            cache.set(plan, summary);
        }
        return summary;
    };
}
